import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updateProfile,
  type User as FirebaseUser,
  type Unsubscribe,
} from 'firebase/auth'
import { auth } from '@/services/firebase'

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}

/**
 * Authenticate a user with email and password via Firebase Auth.
 *
 * Calls `signInWithEmailAndPassword` on the shared `auth` instance.
 * On success the Firebase Auth state changes and `onAuthStateChanged`
 * listeners fire. On failure (wrong password, user not found, etc.)
 * throws a ServiceError whose `code` matches the Firebase Auth error code.
 */
export async function signIn(email: string, password: string): Promise<void> {
  try {
    await signInWithEmailAndPassword(auth, email, password)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Register a new user with email, password, and display name.
 *
 * Calls `createUserWithEmailAndPassword` then immediately calls
 * `updateProfile` to set the displayName on the Firebase Auth user.
 * The `onUserCreate` Cloud Function triggers on the new Auth record
 * and seeds base auth claims (`role: 'user'`) plus an empty `instituteRoles` map.
 *
 * Throws ServiceError on Firebase Auth failure (e.g. email already in use).
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string
): Promise<void> {
  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password)
    await updateProfile(credential.user, { displayName })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Sign out the currently authenticated user via Firebase Auth.
 *
 * Calls `signOut` on the shared `auth` instance. On success the Auth
 * state changes to null and `onAuthStateChanged` listeners fire.
 *
 * Throws ServiceError on failure.
 */
export async function signOutUser(): Promise<void> {
  try {
    await signOut(auth)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Send a password reset email to the given address via Firebase Auth.
 *
 * Calls `sendPasswordResetEmail`. Does not verify whether the email
 * exists — Firebase may silently succeed for non-existent emails
 * depending on project settings.
 *
 * Throws ServiceError on Firebase Auth failure.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Subscribe to Firebase Auth state changes.
 *
 * Wraps `firebaseOnAuthStateChanged` on the shared `auth` instance.
 * The callback receives the Firebase User object when signed in or
 * null when signed out. Returns an unsubscribe function.
 *
 * Used by `AuthContext` to track the current user session.
 */
export function onAuthStateChanged(callback: (user: FirebaseUser | null) => void): Unsubscribe {
  return firebaseOnAuthStateChanged(auth, callback)
}

/**
 * Return the currently signed-in Firebase Auth user, or null if no
 * user is authenticated.
 *
 * Synchronous read of `auth.currentUser`. May return null immediately
 * after page load before the Auth SDK has resolved the persisted session.
 * Prefer `onAuthStateChanged` for reliable session detection.
 */
export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser
}
